import {
  Form,
  FormSubmitButton,
  StateSelectFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { DataTable } from "@/components/ui/shadcn/DataTable/DataTable";
import { getAvailablePhoneNumbersQueryOptions } from "@/lib/data/react-query/firm/firm-phones";
import { SearchAvailablePhoneNumbersSchema } from "@/lib/data/schemas/firm/FirmSchema";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type AvailablePhoneNumber } from "@/lib/types/Firm";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { searchPhoneNumberTableColumns } from "./search-phone-number-columns";

const FORM_ID = "search-phone-numbers-form";
const formSchema = SearchAvailablePhoneNumbersSchema;

interface SearchPhoneNumbersModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSelect: (phone: AvailablePhoneNumber) => void;
}

export function SearchPhoneNumbersModal({
  open,
  setOpen,
  onSelect,
}: SearchPhoneNumbersModalProps) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      areaCode: "",
      pattern: "",
      postalCode: "",
      locality: "",
      region: "",
    },
  });

  const [searchTerm, setSearchTerm] = useState<z.infer<
    typeof formSchema
  > | null>(null);

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    setSearchTerm(data);
  };

  const { data: availablePhones, isLoading: isSearching } = useQuery({
    ...getAvailablePhoneNumbersQueryOptions(
      searchTerm ?? {
        areaCode: "",
        postalCode: "",
        locality: "",
        pattern: "",
        region: "",
      },
    ),
    enabled: open && searchTerm !== null,
  });

  useEffect(() => {
    if (!open) {
      setSearchTerm(null);
      form.reset();
    }
  }, [form, open]);

  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title="Search phone numbers"
        description="Search for available phone numbers to add to your firm"
        isDirty={form.formState.isDirty}
        size="3xl"
        className="z-[109] w-[calc(100vw-2rem)]"
        overlayClassName="z-[108] bg-black/50 backdrop-blur-md"
        footer={
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <FormCancelButton
              setOpen={setOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton
              title="Search"
              disabled={false}
              submitFormId={FORM_ID}
            />
          </div>
        }
      >
        <form
          id={FORM_ID}
          className="flex min-h-0 grow flex-col overflow-hidden"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit(onSubmit)();
          }}
        >
          <div className="overflow-y-auto">
            <fieldset className={slideoverFieldsetClassName}>
              <TextFormField
                label="Area code"
                register={form.register("areaCode")}
              />
              <TextFormField
                label="Postal code"
                register={form.register("postalCode")}
              />
              <StateSelectFormField
                label="State"
                register={form.register("region")}
                country="United States"
                name="region"
                valueType="short"
              />
              <div className="col-span-full">
                <DataTable
                  entityLabelPlural="phone numbers"
                  columns={searchPhoneNumberTableColumns}
                  hideSearchBar
                  hideVisiibilityDropdown
                  data={availablePhones?.data || []}
                  isLoading={isSearching}
                  tableName={tanstackTableNames.phoneNumberSearch}
                  onRowClick={(row) => {
                    onSelect(row.original);
                    setOpen(false);
                  }}
                  noResultsMessage="No phone numbers found"
                />
              </div>
            </fieldset>
          </div>
        </form>
      </AppModal>
    </Form>
  );
}
